/**
 * Query params that hold what the guest entered — dates, guest counts,
 * language. A logo click carries them to the start page so pressing the logo
 * empties the *view*, not the search. Anything else in the URL (categoryAdId,
 * previewCategoryAdId, category filters) describes the page being left and is
 * deliberately dropped.
 */
const PRESERVED_SEARCH_PARAMS = [
  "lang",
  "arrival",
  "departure",
  "adults",
  "teenagers",
  "children",
  "infants",
] as const;

/**
 * Tells the landing mount "keep this search, don't run it": the header shows
 * the dates and guests, the body shows the start page rather than results.
 * Consumed and stripped from the URL by `HeaderProvider` on mount, so it never
 * survives into a link the guest could copy.
 */
export const NO_AUTO_SEARCH_PARAM = "noSearch";

/**
 * The search string for a logo click: the guest's own search, plus the flag
 * that keeps the landing page from re-running it.
 *
 * The flag is only added when both dates are present, because that pair is the
 * only thing that auto-starts a search — a logo click from a site with no dates
 * picked needs no suppression and keeps a clean URL.
 */
export const getLogoNavigationSearch = (search: string): string => {
  const current = new URLSearchParams(search);
  const preserved = new URLSearchParams();

  PRESERVED_SEARCH_PARAMS.forEach((key) => {
    current.getAll(key).forEach((value) => preserved.append(key, value));
  });

  if (current.get("arrival") && current.get("departure")) {
    preserved.set(NO_AUTO_SEARCH_PARAM, "1");
  }

  return preserved.toString();
};

export const getLogoNavigationPath = (
  identifier: string | null | undefined,
  search: string = "",
) => {
  const path = identifier ? `/${identifier}` : "/";
  const query = getLogoNavigationSearch(search);
  return query ? `${path}?${query}` : path;
};

/** Stored site URLs are free-form; force a scheme so the browser doesn't treat them as relative paths. */
export const normalizeWebsiteUrl = (url: string) =>
  /^https?:\/\//i.test(url) ? url : `https://${url}`;

/**
 * Where a logotype click should navigate: the site's website when the
 * per-site setting is enabled and a website is registered, otherwise the
 * WBE start page.
 */
export const getLogoNavigationTarget = (
  identifier: string | null | undefined,
  logotypeLinksToWebsite: boolean | undefined,
  siteUrl: string | null | undefined,
  search: string = "",
) => {
  const website = siteUrl?.trim();
  if (logotypeLinksToWebsite && website) {
    return normalizeWebsiteUrl(website);
  }
  return getLogoNavigationPath(identifier, search);
};
